import React from 'react';
import {
  ArrowRight,
  Building2,
  ClipboardList,
  FileCheck,
  Globe2,
  KeyRound,
  Lock,
  Mail,
  Network,
  ScrollText,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';

type Status = 'live' | 'in progress' | 'roadmap' | 'on request';

const statusStyles: Record<Status, string> = {
  live: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300',
  'in progress': 'border-amber-400/25 bg-amber-400/10 text-amber-300',
  roadmap: 'border-white/15 bg-white/[0.06] text-white/55',
  'on request': 'border-sky-400/25 bg-sky-400/10 text-sky-300',
};

const StatusBadge: React.FC<{ status: Status }> = ({ status }) => (
  <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${statusStyles[status]}`}>
    {status}
  </span>
);

const capabilities: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  body: string;
  status: Status;
  href?: string;
}[] = [
  {
    icon: Users,
    title: 'Organizations, roles, and RBAC',
    body: 'Owner, admin, member, and viewer roles per organization. Invite by email, change roles, remove members; every change lands in the audit log.',
    status: 'live',
    href: '/settings',
  },
  {
    icon: ScrollText,
    title: 'Audit logs',
    body: 'Settings changes, invites, role changes, and membership removals are recorded per user and per org, queryable from the API and settings.',
    status: 'live',
    href: '/settings',
  },
  {
    icon: Wallet,
    title: 'Spend limits',
    body: 'Per-user spend limits are live in settings. Per-workspace limits, capping every member under one budget, are roadmap.',
    status: 'live',
    href: '/settings',
  },
  {
    icon: KeyRound,
    title: 'API key management',
    body: 'Scoped, rotatable keys, plus bring-your-own provider keys for the gateway. Keys are stored hashed; plaintext is shown once.',
    status: 'live',
    href: '/api-keys',
  },
  {
    icon: Lock,
    title: 'SSO / SAML + SCIM',
    body: 'Google and GitHub sign-in are live. SAML SSO and SCIM directory sync are roadmap for the contact-sales tier. Talk to us if this is a blocker.',
    status: 'roadmap',
  },
  {
    icon: Network,
    title: 'Private networking',
    body: 'Dedicated network segments and private egress between your apps, addons, and agents. Roadmap; today each container is isolated with scoped credentials.',
    status: 'roadmap',
  },
  {
    icon: Globe2,
    title: 'Data residency',
    body: 'A default GPU region (US or EU) is selectable in settings. Hard residency guarantees that pin data and inference to a jurisdiction are roadmap.',
    status: 'roadmap',
  },
  {
    icon: FileCheck,
    title: 'BAA / DPA',
    body: 'A standard DPA is available on request. Healthcare BAAs are handled case by case on the contact-sales tier.',
    status: 'on request',
  },
];

const complianceRows = [
  {
    name: 'SOC 2 Type II',
    status: 'in progress' as Status,
    detail: 'Controls are being implemented and evidence collected; no report yet. We will not claim certification before it exists.',
  },
  {
    name: 'Data processing agreement (DPA)',
    status: 'on request' as Status,
    detail: 'Standard DPA covering GDPR processor obligations, available for any paid plan via enterprise@app.nz.',
  },
  {
    name: 'Data export and deletion',
    status: 'live' as Status,
    detail: 'Export or delete your account from settings; both are audit-logged.',
  },
  {
    name: 'Subprocessor list',
    status: 'live' as Status,
    detail: 'Published on the security page, kept current as infrastructure changes.',
  },
];

export const EnterprisePage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/58">
            <Building2 size={12} className="text-sky-400" />
            Enterprise
          </div>
          <h1 className="mt-6 max-w-[16ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Organizations, roles, audit logs, and spend limits
          </h1>
          <p className="mt-6 max-w-xl text-base leading-8 text-white/72">
            The controls available today, each labeled live, in progress, or roadmap.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="mailto:enterprise@app.nz" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              <Mail size={16} /> Talk to us
            </a>
            <a href="/security" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              <ShieldCheck size={16} /> Security posture
            </a>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">What teams get today</p>
          <ul className="mt-4 space-y-3">
            {[
              'Orgs with owner / admin / member / viewer roles',
              'Email invites with expiring tokens, audit-logged',
              'Per-user spend limits and analytics opt-out',
              'API keys stored hashed, rotatable from /api-keys',
              'Shared org agent feed: every run across the team',
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm leading-6 text-white/72">
                <ShieldCheck size={15} className="mt-1 shrink-0 text-emerald-400" />
                {line}
              </li>
            ))}
          </ul>
          <a href="/settings" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-sky-400 hover:text-sky-300">
            Open settings <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">capabilities</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Feature status</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Live means it works now. Roadmap means we are building it, and we will scope timelines with you.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c) => {
            const Icon = c.icon;
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <Icon size={20} className="text-sky-400" />
                  <StatusBadge status={c.status} />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">{c.title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/65">{c.body}</p>
              </>
            );
            return c.href ? (
              <a key={c.title} href={c.href} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-sky-400/40">
                {inner}
              </a>
            ) : (
              <div key={c.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">compliance posture</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Compliance status</h2>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-[11px] font-bold uppercase tracking-[0.16em] text-white/45">
                <th className="px-5 py-4">Item</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Detail</th>
              </tr>
            </thead>
            <tbody>
              {complianceRows.map((r) => (
                <tr key={r.name} className="border-b border-white/5 last:border-0">
                  <td className="px-5 py-4 font-semibold text-white/85">{r.name}</td>
                  <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-4 leading-6 text-white/65">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <ClipboardList size={18} className="mt-0.5 shrink-0 text-sky-400" />
          <p className="text-sm leading-7 text-white/65">
            Security questionnaires, DPAs, and scoped pilots go to{' '}
            <a href="mailto:enterprise@app.nz" className="font-semibold text-sky-400 hover:text-sky-300">enterprise@app.nz</a>{' '}
            — the people who run the infrastructure.
          </p>
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
        <div className="relative">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Start with an organization</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            Create an org, invite your team, and tell us which roadmap item you need.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="/settings" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              <Users size={16} /> Create your org
            </a>
            <a href="mailto:enterprise@app.nz" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              <Mail size={16} /> enterprise@app.nz
            </a>
          </div>
        </div>
      </div>
    </section>
  </main>
);

export default EnterprisePage;
